/**
 * Os filtros do histórico de documentos (#344).
 *
 * Duas faixas, e a separação é de propósito: em cima o recorte do que se procura (empresa,
 * tipo, período), embaixo os atalhos de situação — que são a pergunta mais frequente de quem
 * abre a tela ("o que está para vencer?") e por isso ficam a um clique, não dentro de um
 * seletor.
 *
 * As contagens dos atalhos vêm do SERVIDOR, calculadas sobre o filtro inteiro. Contar a
 * página carregada daria número errado da segunda página em diante.
 */

import React, { useEffect, useState } from 'react';
import { Button, Chip, Stack, TextField, Typography } from '@mui/material';

import { InlineFilterBar, InlineSelect } from '../../../components/common';
import { CompanySelectField } from '../../../components/forms/CompanySelectField';
import { documentService } from '../../../services/documentService';
import type {
  DocumentTypeSummary,
  EmissionSummary,
  EmissionSummaryStatus,
} from '../../../types/document';
import type { SituacaoDaEmissao } from '../situacao';

/** O estado dos filtros como a tela o guarda: tudo em texto, como os campos entregam. */
export interface FiltrosDoHistorico {
  companyId: string;
  type: string;
  periodStart: string;
  periodEnd: string;
  /** Vazio = "Todas". */
  status: '' | EmissionSummaryStatus;
}

export const FILTROS_VAZIOS: FiltrosDoHistorico = {
  companyId: '',
  type: '',
  periodStart: '',
  periodEnd: '',
  status: '',
};

/**
 * Há recorte aplicado além da empresa.
 *
 * A empresa fica de fora porque ela não é um filtro: é o contexto de quem está olhando, e o
 * vazio dela já diz "nenhum documento emitido para esta empresa".
 */
export function temFiltroAplicado(filtros: FiltrosDoHistorico): boolean {
  return Boolean(filtros.type || filtros.periodStart || filtros.periodEnd || filtros.status);
}

/** O chip da situação: o texto já vem calculado, com o quanto falta. */
export const SituacaoChip: React.FC<{ situacao: SituacaoDaEmissao }> = ({ situacao }) => (
  <Chip
    size="small"
    label={situacao.rotulo}
    color={situacao.cor}
    variant={situacao.status === 'semValidade' ? 'outlined' : 'filled'}
  />
);

interface AtalhoDeSituacao {
  valor: '' | EmissionSummaryStatus;
  rotulo: string;
  cor: 'default' | 'warning' | 'error';
}

export interface DocumentHistoryFiltersProps {
  filtros: FiltrosDoHistorico;
  onChange: (filtros: FiltrosDoHistorico) => void;
  summary: EmissionSummary;
  carregando?: boolean;
}

export const DocumentHistoryFilters: React.FC<DocumentHistoryFiltersProps> = ({
  filtros,
  onChange,
  summary,
  carregando = false,
}) => {
  const [tipos, setTipos] = useState<DocumentTypeSummary[]>([]);

  useEffect(() => {
    let ativo = true;
    const empresa = filtros.companyId === '' ? undefined : Number(filtros.companyId);

    documentService
      .listTypes(empresa)
      .then((lista) => {
        if (ativo) setTipos(lista);
      })
      .catch(() => {
        if (ativo) setTipos([]);
      });

    return () => {
      ativo = false;
    };
  }, [filtros.companyId]);

  const trocar = (parte: Partial<FiltrosDoHistorico>): void => onChange({ ...filtros, ...parte });

  const atalhos: AtalhoDeSituacao[] = [
    { valor: '', rotulo: 'Todas', cor: 'default' },
    { valor: 'vigente', rotulo: 'Vigentes', cor: 'default' },
    { valor: 'aVencer', rotulo: `A vencer · ${summary.aVencer}`, cor: 'warning' },
    { valor: 'vencido', rotulo: `Vencidos · ${summary.vencido}`, cor: 'error' },
  ];

  return (
    <Stack spacing={2}>
      <InlineFilterBar
        loading={carregando}
        actions={
          <Button
            size="small"
            onClick={() => onChange({ ...FILTROS_VAZIOS, companyId: filtros.companyId })}
            disabled={!temFiltroAplicado(filtros)}
          >
            Limpar
          </Button>
        }
      >
        {/*
          Trocar de empresa LIMPA o tipo: a lista de tipos é recarregada para a empresa nova,
          e um tipo que ela não tem sumiria do seletor enquanto a consulta continuava
          filtrando por ele — a tela mostraria lista vazia sem nada explicando o recorte.
        */}
        <CompanySelectField
          value={filtros.companyId}
          onChange={(valor) => trocar({ companyId: valor, type: '' })}
          required={false}
        />

        <InlineSelect
          label="Tipo"
          value={filtros.type}
          emptyLabel="Todos"
          options={tipos.map((tipo) => ({ value: tipo.type, label: tipo.label }))}
          onChange={(valor) => trocar({ type: valor })}
        />

        <TextField
          label="Período de"
          type="date"
          size="small"
          value={filtros.periodStart}
          onChange={(evento) => trocar({ periodStart: evento.target.value })}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="até"
          type="date"
          size="small"
          value={filtros.periodEnd}
          onChange={(evento) => trocar({ periodEnd: evento.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </InlineFilterBar>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          Situação
        </Typography>
        {atalhos.map((atalho) => (
          <Chip
            key={atalho.rotulo}
            label={atalho.rotulo}
            size="small"
            color={filtros.status === atalho.valor ? 'primary' : atalho.cor}
            variant={filtros.status === atalho.valor ? 'filled' : 'outlined'}
            aria-pressed={filtros.status === atalho.valor}
            onClick={() => trocar({ status: atalho.valor })}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default DocumentHistoryFilters;
